import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getExplorerStats, getLatestBlocks, getLatestTxs } from "@/lib/api";
import { EXPLORER } from "@/constants/testIds";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Blocks, ArrowRightLeft, Clock, Zap, TrendingUp, Search, Gauge, Users, Fuel, Rocket, Activity } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { TradeTicker } from "@/components/explorer/TradeTicker";
import { PriceStrip } from "@/components/prices/LivePrices";
import { FADE_UP, FADE_UP_SM, HERO_TRANSITION } from "@/lib/chartStyles";

function formatTimeAgo(ts) {
  const diff = Math.floor(Date.now() / 1000 - ts);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function truncate(str, start = 8, end = 6) {
  if (!str || str.length <= start + end) return str;
  return `${str.slice(0, start)}...${str.slice(-end)}`;
}

function formatFMI(weiStr) {
  const val = Number(weiStr) / 1e18;
  return val.toFixed(4);
}

const statIcons = [
  { key: "latestBlock", label: "Latest Block", icon: Blocks, format: v => v?.toLocaleString() },
  { key: "tps", label: "TPS", icon: Zap, format: v => v },
  { key: "fmiPrice", label: "FMI Price", icon: TrendingUp, format: v => `$${v}` },
  { key: "gasPrice", label: "Gas Price", icon: Fuel, format: v => v },
  { key: "activeValidators", label: "Validators", icon: Users, format: v => v },
  { key: "averageBlockTime", label: "Block Time", icon: Gauge, format: v => `${v}s` },
];

const STAGGER = [];
const staggerTransition = (i) => (STAGGER[i] ??= { delay: i * 0.06, duration: 0.35 });

export default function ExplorerHome() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: getExplorerStats, refetchInterval: 4000 });
  const { data: blocks } = useQuery({ queryKey: ["latestBlocks"], queryFn: () => getLatestBlocks(8), refetchInterval: 4000 });
  const { data: txs } = useQuery({ queryKey: ["latestTxs"], queryFn: () => getLatestTxs(8), refetchInterval: 4000 });

  const handleSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    if (q.startsWith("0x") && q.length === 66) {
      navigate(`/tx/${q}`);
    } else if (q.startsWith("0x") && q.length === 42) {
      navigate(`/address/${q}`);
    } else if (/^\d+$/.test(q)) {
      navigate(`/block/${q}`);
    } else {
      toast.error("Invalid search query. Enter a block number, tx hash or address.");
    }
  };

  const isDemo = stats?.isDemo;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Hero Search */}
      <div className="text-center py-8 sm:py-12">
        <motion.h1
          {...FADE_UP}
          transition={HERO_TRANSITION}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mb-3"
        >
          FMI Smart Chain <span className="text-[#D4AF37]">Explorer</span>
        </motion.h1>
        <p className="text-zinc-400 text-base mb-6">
          Search blocks, transactions, and addresses on FSC
        </p>
        {isDemo && (
          <Badge data-testid={EXPLORER.demoBadge} className="demo-badge mb-4">Demo Data</Badge>
        )}
        <div className="flex justify-center gap-3 mb-6">
          <Link to="/get-started" data-testid="hero-get-started-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#F3E5AB] transition-transform active:scale-95">
            <Rocket size={15} /> Get started in one click
          </Link>
          <Link to="/status" data-testid="hero-status-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#2A2411] text-zinc-300 text-sm font-medium hover:border-[#D4AF37]/50 hover:text-white transition-colors">
            <Activity size={15} /> Network status
          </Link>
        </div>
        <form onSubmit={handleSearch} className="max-w-2xl mx-auto flex gap-2">
          <Input
            data-testid={EXPLORER.searchInput}
            placeholder="Search by address / tx hash / block number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-[#0A0A0A] border-[#2A2411] text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#D4AF37]/50 h-12 font-mono text-sm"
          />
          <Button
            data-testid={EXPLORER.searchBtn}
            type="submit"
            className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] h-12 px-6 rounded-md transition-transform active:scale-95"
          >
            <Search size={18} />
          </Button>
        </form>
      </div>

      <PriceStrip />
      <TradeTicker />

      {/* Stats Grid */}
      <div data-testid={EXPLORER.statsGrid} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {statIcons.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.key}
              {...FADE_UP_SM}
              transition={staggerTransition(i)}
            >
              <Card className="border-[#2A2411] bg-[#0A0A0A] hover:border-[#D4AF37]/30 card-hover">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={14} className="text-[#D4AF37]" />
                    <span className="text-xs text-zinc-500 uppercase tracking-wider">{s.label}</span>
                  </div>
                  <p className="stat-value font-display font-bold text-xl text-white">
                    {stats ? s.format(stats[s.key]) : "--"}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Latest Blocks & Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Latest Blocks */}
        <Card className="border-[#2A2411] bg-[#0A0A0A]">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-display">
              <Blocks size={16} className="text-[#D4AF37]" />
              Latest Blocks
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid={EXPLORER.latestBlocks}>
              <TableHeader>
                <TableRow className="border-[#2A2411] hover:bg-transparent">
                  <TableHead className="text-zinc-500 text-xs">Block</TableHead>
                  <TableHead className="text-zinc-500 text-xs">Age</TableHead>
                  <TableHead className="text-zinc-500 text-xs">Txs</TableHead>
                  <TableHead className="text-zinc-500 text-xs">Miner</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blocks?.map((b, i) => (
                  <TableRow key={b.number} className="border-[#2A2411] stagger-item" style={{ animationDelay: `${i * 50}ms` }}>
                    <TableCell>
                      <Link to={`/block/${b.number}`} data-testid={EXPLORER.blockNumber} className="table-link font-mono text-sm">
                        {b.number.toLocaleString()}
                      </Link>
                    </TableCell>
                    <TableCell className="text-zinc-400 text-sm">{formatTimeAgo(b.timestamp)}</TableCell>
                    <TableCell className="text-zinc-300 text-sm">{b.transactionCount}</TableCell>
                    <TableCell>
                      <Link to={`/address/${b.miner}`} className="table-link font-mono text-xs">
                        {truncate(b.miner, 6, 4)}
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Latest Transactions */}
        <Card className="border-[#2A2411] bg-[#0A0A0A]">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-display">
              <ArrowRightLeft size={16} className="text-[#D4AF37]" />
              Latest Transactions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid={EXPLORER.latestTxs}>
              <TableHeader>
                <TableRow className="border-[#2A2411] hover:bg-transparent">
                  <TableHead className="text-zinc-500 text-xs">Tx Hash</TableHead>
                  <TableHead className="text-zinc-500 text-xs">From</TableHead>
                  <TableHead className="text-zinc-500 text-xs">To</TableHead>
                  <TableHead className="text-zinc-500 text-xs text-right">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txs?.map((t, i) => (
                  <TableRow key={t.hash} className="border-[#2A2411] stagger-item" style={{ animationDelay: `${i * 50}ms` }}>
                    <TableCell>
                      <Link to={`/tx/${t.hash}`} data-testid={EXPLORER.txHash} className="table-link font-mono text-xs">
                        {truncate(t.hash, 8, 6)}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link to={`/address/${t.from}`} className="table-link font-mono text-xs">
                        {truncate(t.from, 6, 4)}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link to={`/address/${t.tokenTransfer?.to || t.to}`} className="table-link font-mono text-xs">
                        {truncate(t.tokenTransfer?.to || t.to, 6, 4)}
                      </Link>
                      {t.toIsContract && !t.tokenTransfer && (
                        <span data-testid="tx-to-contract-chip" className="ml-1.5 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37]">CONTRACT</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-zinc-300 font-mono text-xs">
                      {t.tokenTransfer ? `${t.tokenTransfer.amount.toLocaleString()} ${t.tokenTransfer.token}` : `${formatFMI(t.value)} FMI`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
